import React, { useState, useMemo } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { PlusIcon } from 'lucide-react';

import { cn } from '@epoch-ui/lib/utils';
import { Button } from '@epoch-ui/components/Button';
import { Skeleton } from '@epoch-ui/components/Skeleton/skeleton';
import { SortIndicator, SortOrder } from '@epoch-components/tables/SortIndicator';
import { getServiceConfig } from 'config/serviceConfig';
import { useEditableEvent } from 'pages/dashboard/overview/events/editor/hooks/useEditableEvent';
import { useCreateDraftSessionSheet } from '@event-editor/hooks/eventEditorState';
import { useSessionsQuery } from '@event-editor/hooks/useSessionsQuery';

import { EventSessionListItem } from './components/EventSessionListItem';
import { EditSessionRegistrationLimitToggle } from './components/EditSessionRegistrationLimitToggle';

const MAX_ALLOWED_UPCOMING_SESSIONS = getServiceConfig('EVENT', 'MAX_SESSIONS_ALLOWED');

const EventSessions: React.FC = () => {
  const event = useEditableEvent();
  const createDraftSessionSheet = useCreateDraftSessionSheet();
  const [sortOrder, setSortOrder] = useState<SortOrder | undefined>();

  const sessionsQuery = useSessionsQuery(event, {
    sortParams: sortOrder ? { sortBy: 'start_datetime', sortOrder } : undefined,
    placeholderData: keepPreviousData
  });

  const sessions = sessionsQuery.data || [];
  const numOfUpcomingActiveSessions = useMemo(
    () => sessions.filter((session) => session.isPublished && !session.hasEnded).length,
    [sessions]
  );

  const isCancelled = !!event.scheduledDeletionAt;
  const isDisabled = event.hasEnded || isCancelled;
  const maxUpcomingSessionsReached = numOfUpcomingActiveSessions >= MAX_ALLOWED_UPCOMING_SESSIONS;

  return (
    <div className="tw-flex tw-flex-col tw-items-start tw-gap-4">
      {/* Heading  */}
      <div className="tw-flex tw-w-full tw-items-center tw-justify-between">
        <h2 className="tw-m-0 tw-text-xl tw-font-medium tw-leading-7">Sessions</h2>
        {sessions.length > 1 && (
          <Button
            variant="secondary"
            size="xxs"
            className="tw-rounded-xl"
            disabled={isDisabled}
            onClick={() =>
              setSortOrder((prev) => {
                if (prev === SortOrder.ASC) {
                  return SortOrder.DESC;
                }
                if (prev === SortOrder.DESC) {
                  return undefined;
                }
                return SortOrder.ASC;
              })
            }
          >
            Sort by start time
            <SortIndicator className="tw-ml-1" size={8} sortOrder={sortOrder} />
          </Button>
        )}
      </div>

      {/* Initial loading state */}
      {sessionsQuery.isPending && <Skeleton className="tw-h-36 tw-w-full tw-rounded-2xl" />}

      {/* Empty state */}
      {sessions.length === 0 && (
        <div className="tw-flex tw-h-28 tw-w-full tw-items-center tw-justify-center tw-rounded-2xl tw-border-[1.5px] tw-border-dashed">
          <Button
            variant="secondary"
            size="sm"
            className="tw-rounded-xl"
            onClick={() => createDraftSessionSheet.open()}
          >
            <PlusIcon size={16} className="tw-mr-1" />
            Add session
          </Button>
        </div>
      )}

      {/* Sessions list */}
      {sessions.length > 0 && (
        <>
          <div className="tw-flex tw-w-full tw-flex-col tw-gap-3">
            {sessions.map((session) =>
              sessionsQuery.isFetching ? (
                <Skeleton key={session.id} className="tw-h-[70px] tw-w-full tw-rounded-2xl" />
              ) : (
                <EventSessionListItem
                  key={session.id}
                  session={session}
                  className={cn(
                    !session.hasEnded &&
                      maxUpcomingSessionsReached &&
                      'tw-border-destructive-foreground'
                  )}
                />
              )
            )}

            {maxUpcomingSessionsReached && (
              <p className="tw-m-0 tw-p-0 tw-text-xs tw-font-normal tw-leading-4 tw-text-destructive-foreground">
                Maximum {MAX_ALLOWED_UPCOMING_SESSIONS} upcoming sessions
              </p>
            )}
          </div>

          <Button
            size="xxs"
            variant="primary"
            disabled={isDisabled}
            className="tw-rounded-xl"
            onClick={() => createDraftSessionSheet.open()}
          >
            <PlusIcon size={16} className="tw-mr-1" />
            Add session
          </Button>

          <EditSessionRegistrationLimitToggle />
        </>
      )}
    </div>
  );
};

export { EventSessions };
